import React, { useEffect, useState, useTransition } from 'react';
import {
  Icons,
  Progress,
  Separator,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  Button,
  TruncatedText,
} from '@ais-ui/ui-core';
import { PiEye, PiEyeClosed, PiProhibit, PiArrowSquareOut, PiHourglassMedium } from 'react-icons/pi';
import { FaCircleXmark } from 'react-icons/fa6';
import { Job, ReportTemplate } from '@ais-ui/websockets';
import { useDateTimeFormat } from '../conversions/useDateFormat';
import { ReportItemDropDownMenu } from './ReportItemDropDownMenu';
import { hasVisualOutput } from './useManageReportProcessing';
import { useReportContext } from './ReportContext';
import { useLocalization } from '@ais-ui/locale-management';
import { openPopupOnSecondaryScreen, PopupType } from '../Popup';
import { useReportActions } from './useReportActions';

interface ReportItemProps {
  report: Job;
  reportTemplate: ReportTemplate;
}

export const ReportItem = React.memo(
  ({ report, reportTemplate }: ReportItemProps) => {
    const [progress, setProgress] = useState<number>(report.percentComplete);
    const { formatDateTimeWithTimeZone } = useDateTimeFormat();
    const { messages } = useLocalization();
    const { selectedJob, isWaiting } = useReportContext();
    const { showReport, hideReport } = useReportActions();

    const failureReason = report.failureReason.replace(': internal error', '');

    const isFinished =
      report.status === 'FINISHED' || report.status === 'FINISHED_SCHEDULED';
    const isFailed = report.status === 'FAILED';
    const isAborted = report.status === 'ABORTED';
    const isExecuting = report.status === 'EXECUTING' || report.status === 'PENDING';
    
    const canShow = isFinished && hasVisualOutput(reportTemplate.type);
    const isVisible = selectedJob?.uuid === report.uuid;
    
    // Use transition to show loading state immediately on click
    const [isPending, startTransition] = useTransition();
    const [localLoading, setLocalLoading] = useState(false);
    const isLoading = (isVisible && isWaiting) || isPending || localLoading;

    useEffect(() => {
      const timer = setTimeout(() => setProgress(report.percentComplete), 500);
      return () => clearTimeout(timer);
    }, [report.percentComplete]);

    const handleVisibilityClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!canShow) return;

      if (isVisible) {
        hideReport();
      } else {
        setLocalLoading(true);
        startTransition(() => {
          showReport(report, reportTemplate);
          // Reset local loading after a short delay to allow context to update
          setTimeout(() => setLocalLoading(false), 100);
        });
      }
    };

    const renderStatusIcon = () => {
      // Executing/Pending - show hourglass icon (static, progress bar shows activity)
      if (isExecuting) {
        return (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="flex items-center justify-center size-7">
                <PiHourglassMedium className="size-4 text-blue-500" />
              </span>
            </TooltipTrigger>
            <TooltipContent>
              {report.status === 'EXECUTING'
                ? messages.reportsItemExecuting
                : messages.reportsItemPending}
            </TooltipContent>
          </Tooltip>
        );
      }

      // Failed - show error icon with reason
      if (isFailed) {
        return (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="flex items-center justify-center size-7">
                <FaCircleXmark className="size-4 text-destructive" />
              </span>
            </TooltipTrigger>
            <TooltipContent>
              {failureReason || messages.reportsItemFailed}
            </TooltipContent>
          </Tooltip>
        );
      }

      // Aborted - show aborted icon
      if (isAborted) {
        return (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="flex items-center justify-center size-7">
                <PiProhibit className="size-4 text-orange-500" />
              </span>
            </TooltipTrigger>
            <TooltipContent>{messages.reportsItemAborted}</TooltipContent>
          </Tooltip>
        );
      }

      // Finished - show eye icon and popup icon (clickable if has visual output)
      if (isFinished) {
        if (!hasVisualOutput(reportTemplate.type)) {
          return (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="flex items-center justify-center size-7">
                  <PiEyeClosed className="size-4 text-muted-foreground" />
                </span>
              </TooltipTrigger>
              <TooltipContent>
                {messages.reportsItemNoVisualOutput}
              </TooltipContent>
            </Tooltip>
          );
        }

        return (
          <>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  id={`show-report-button-${report.uuid}`}
                  size="smIcon"
                  variant="ghost"
                  onClick={handleVisibilityClick}
                  className="size-7"
                >
                  {isLoading ? (
                    <Icons.spinner className="size-4 animate-spin" />
                  ) : isVisible ? (
                    <PiEye className="size-4" />
                  ) : (
                    <PiEyeClosed className="size-4" />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {isLoading
                  ? messages.reportsItemLoadingReport
                  : isVisible
                    ? messages.reportsItemHideReport
                    : messages.reportsItemShowReport}
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  id={`open-report-popup-button-${report.uuid}`}
                  size="smIcon"
                  variant="ghost"
                  onClick={(e) => {
                    e.stopPropagation();
                    openPopupOnSecondaryScreen(PopupType.Report, { jobId: report.uuid });
                  }}
                  className="size-7"
                >
                  <PiArrowSquareOut className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {messages.reportsItemOpenInNewWindow}
              </TooltipContent>
            </Tooltip>
          </>
        );
      }

      return null;
    };

    return (
      <div className="w-full border-b border-border last:border-b-0 overflow-hidden">
        <div className="grid grid-cols-[1fr_34px] items-center group/report-item py-2 px-3">
          {/* Left side: Status/Visibility icon + Separator + Report info */}
          <div className="flex items-center min-w-0">
            <div className="flex flex-col items-center shrink-0">
              <div className="flex items-center">
                {renderStatusIcon()}
                <Separator orientation="vertical" className="h-6 shrink-0" />
              </div>
              {isExecuting && (
                <p className="text-xs text-muted-foreground mt-1">{progress}%</p>
              )}
            </div>
            <div className="min-w-0 ml-2 flex-1">
              <TruncatedText
                text={report.name}
                className="text-sm font-medium leading-none"
                maxWidth="100%"
              />
              <p className="text-xs text-muted-foreground truncate mt-0.5">
                {formatDateTimeWithTimeZone(report.creationdate)}
              </p>
              {isExecuting && (
                <Progress value={progress} className="h-1 mt-1" />
              )}
            </div>
          </div>

          {/* Right side: Kebab menu */}
          <div className="flex items-center justify-end">
            <ReportItemDropDownMenu
              report={report}
              reportTemplate={reportTemplate}
            />
          </div>
        </div>
      </div>
    );
  }
);

ReportItem.displayName = 'ReportItem';
